import { MOBILE_CANVAS_MAX_SIZE, allowedUploadingTypes, IMAGE_MIME_TYPES, UPLOAD_WEB_CONCURRENCY, UPLOAD_MOBILE_CONCURRENCY, API_WEB_CONCURRENCY, API_MOBILE_CONCURRENCY } from 'pages/Batch/configs';
import { v4 as uuid } from 'uuid';

import { BatchDataStatus } from 'configs/constants';

import type { IBatchData, IBatchFileData, IBatchFileImage, IBatchImage } from 'types/store';

import loadingImage from 'utils/images/loadingImage';

// import { getContext } from 'configs/context';
import applyMobileCanvasSizeLimit from './applyMobileCanvasSizeLimit';

export type ResizingParams = {
  fromX: number;
  fromY: number;
  width: number;
  height: number;
};

export const downloadImage = async (url: string, fileName = '') => {
  const blob = await fetch(url).then(r => r.blob());
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const isRemoteUrl = (url: unknown): url is string => typeof url === 'string' && (url.startsWith('https://') || url.startsWith('http://'));

export const updateObjectUrl = ({ src, blob, width, height }: IBatchFileImage): IBatchImage => ({
  src: blob ? URL.createObjectURL(blob) : src,
  type: blob?.type,
  width,
  height,
});

const tryLoadImage = async (src: string): Promise<HTMLImageElement | null> => {
  if (!src) return null;
  try {
    return await loadingImage(src);
  } catch {
    return null;
  }
};

const withDims = (img: IBatchImage, loaded: HTMLImageElement): IBatchImage => (img.width && img.height ? img : { ...img, width: loaded.naturalWidth, height: loaded.naturalHeight });

// Validates persisted items by loading each URL. Drops items whose source is dead
// (unrecoverable). For items where only result / pendingResult have died, resets
// result -> source and strips pendingResult + asyncJob so the original image is
// salvaged. Also backfills width/height using the same load result.
export const sanitisePersistedItems = async (items: IBatchData[]): Promise<IBatchData[]> => {
  const processed = await Promise.all(
    items.map(async (item): Promise<IBatchData | null> => {
      if (!item) return null;

      const resultMatchesSource = item.result.src === item.source.src;
      const [sourceImg, resultImg, pendingImg] = await Promise.all([
        tryLoadImage(item.source.src),
        resultMatchesSource ? Promise.resolve(null) : tryLoadImage(item.result.src),
        item.pendingResult ? tryLoadImage(item.pendingResult.src) : Promise.resolve(null),
      ]);

      if (!sourceImg) return null;

      const source = withDims(item.source, sourceImg);
      const resultAlive = resultMatchesSource || !!resultImg;
      const result = resultAlive ? withDims(item.result, resultImg ?? sourceImg) : source;

      const pendingExisted = !!item.pendingResult;
      const pendingAlive = pendingExisted ? !!pendingImg : true;
      const pendingResult = pendingExisted && pendingAlive && item.pendingResult && pendingImg ? withDims(item.pendingResult, pendingImg) : undefined;
      const asyncJob = pendingExisted && !pendingAlive ? undefined : item.asyncJob;

      const isProcessed = resultAlive ? item.isProcessed : false;
      const status = resultAlive && pendingAlive ? item.status : BatchDataStatus.PENDING;

      return { ...item, source, result, pendingResult, asyncJob, isProcessed, status };
    }),
  );

  return processed.filter((x): x is IBatchData => x !== null);
};

export const prepareFilesForBatch = async (files: File[]): Promise<IBatchFileData[]> => {
  const filteredFiles = files.filter(file => allowedUploadingTypes.includes(file.type));
  return Promise.all(
    filteredFiles.map(async (file: File) => {
      const objectURL = URL.createObjectURL(file);
      const image = await loadingImage(objectURL);
      const width = image.naturalWidth;
      const height = image.naturalHeight;

      return {
        key: uuid(),
        status: BatchDataStatus.PENDING,
        statusIndicatorMode: 'both' as const,
        source: {
          src: objectURL,
          type: file.type,
          blob: file,
          width,
          height,
        },
        result: {
          src: objectURL,
          type: file.type,
          width,
          height,
        },
        isProcessed: false,
        name: file.name,
      };
    }),
  );
};

export const getResizingParams = (originalWidth: number, originalHeight: number, resizeWidth: number, resizeHeight: number): ResizingParams => {
  const ratio = Math.max(originalWidth / resizeWidth, originalHeight / resizeHeight);
  const fromX = (resizeWidth - originalWidth / ratio) / 2;
  const fromY = (resizeHeight - originalHeight / ratio) / 2;
  return {
    fromX,
    fromY,
    width: originalWidth / ratio,
    height: originalHeight / ratio,
  };
};

export const generateResizedImage = async (item: IBatchFileData, worker: Worker & { processing?: boolean }): Promise<{ result: string; type: string; width: number; height: number }> => {
  const maxMobileCanvasSizeSquared = Math.pow(MOBILE_CANVAS_MAX_SIZE, 2);
  const blobURL = item.source?.src || '';
  const blobFile = item.source?.blob;

  if (!blobFile) {
    throw new Error('Blob file doesnt exist');
  }

  const img = await loadingImage(blobURL);

  // Skip the item resizing if it doesnt need to be resized
  if (img.width * img.height <= maxMobileCanvasSizeSquared) {
    return { result: blobURL, type: blobFile.type, width: img.width, height: img.height };
  }

  // Apply mobile canvas size limit
  const { width: resizedWidth, height: resizedHeight } = applyMobileCanvasSizeLimit(img.width, img.height);

  // Convert blob to ArrayBuffer
  const arrayBuffer = await blobFile.arrayBuffer();

  // Function to handle the worker processing
  const processWithWorker = (resolve: (value: { result: string; type: string; width: number; height: number }) => void) => {
    if (worker.processing) {
      // Retry after a short delay
      setTimeout(() => processWithWorker(resolve), 100);
    } else {
      worker.processing = true;
      worker.postMessage({ arrayBuffer, resizedWidth, resizedHeight });
      worker.onmessage = e => {
        worker.processing = false;
        const resultBlob = new Blob([e.data.resultBuffer], {
          type: IMAGE_MIME_TYPES.WEBP,
        });
        const result = URL.createObjectURL(resultBlob);
        resolve({ result, type: resultBlob.type, width: resizedWidth, height: resizedHeight });
      };
    }
  };

  return new Promise(resolve => processWithWorker(resolve));
};

export const getResizedFilesForBatch = async (items: IBatchFileData[]) => {
  // Worker source lives on the miniapp's origin, which differs from the host page
  // under module federation. Browsers refuse cross-origin Worker scripts, so we
  // fetch the source (CORS-enabled on the remote) and instantiate via a same-origin Blob URL.
  const workerSrcUrl = new URL('./resizingWorker.js', import.meta.url);
  const workerSource = await fetch(workerSrcUrl).then(r => r.text());
  const workerBlobUrl = URL.createObjectURL(new Blob([workerSource], { type: 'application/javascript' }));
  const worker = new Worker(workerBlobUrl);

  const resizedItems = await Promise.all(
    items.map(async (item: IBatchFileData) => {
      const { result, type: blobType, width, height } = await generateResizedImage(item, worker);
      const prevBlobURL = item.source?.src || '';

      // Remove old blob url if it was resized
      if (result && result !== prevBlobURL) {
        URL.revokeObjectURL(prevBlobURL);
      }

      const blob = await fetch(result).then(response => response.blob());

      return {
        ...item,
        result: { ...item.result, src: result, type: blobType, width, height },
        source: { src: result, blob: new Blob([blob], { type: blobType }), type: blobType, width, height },
      };
    }),
  );

  worker.terminate();
  URL.revokeObjectURL(workerBlobUrl);
  return resizedItems;
};

export enum ConcurrentOperation {
  UPLOAD,
  API,
}
export const getConcurrencyLevel = (operation: ConcurrentOperation) => {
  interface NavigatorConnection {
    effectiveType?: string | null;
  }

  interface NavigatorWithConnection {
    connection?: NavigatorConnection | null;
  }

  const type = (navigator as NavigatorWithConnection).connection?.effectiveType || '4g';
  // const isMobile = getContext().handlers.isIOS() || getContext().handlers.isAndroid();
  const isMobile = false;
  const isSlowNetwork = ['2g', 'slow-2g', '3g'].includes(type);

  if (operation === ConcurrentOperation.UPLOAD) {
    return isSlowNetwork ? 1 : isMobile ? UPLOAD_MOBILE_CONCURRENCY : UPLOAD_WEB_CONCURRENCY;
  }
  return isSlowNetwork ? 3 : isMobile ? API_MOBILE_CONCURRENCY : API_WEB_CONCURRENCY;
};
